import http from '@/utils/httpRequestHelper';

/**
 * Submit render data for processing.
 * @param {FormData} formData - The form data containing the image and other fields.
 * @param {string} colorId - The selected HSV color ID.
 * @returns {Promise} - Promise resolving to the server response.
 */
export const submitRender = (formData, colorId) =>
    http.postRequest(`/ai/renderImage?colorId=${colorId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

// formData may include a `miniMode` field ("true"/"false") to render a fast
// low-res preview instead of the full-size output (resolution set backend-side).
export const submitBatchRender = (formData) =>
    http.postRequest('/ai/renderImage/batch', formData, {
        headers: {'Content-Type': 'multipart/form-data'}
    });

export const submitBatchRandomRender = (formData) =>
    http.postRequest('/ai/renderImage/randomMatch', formData, {
        headers: {'Content-Type': 'multipart/form-data'}
    });

export const submitBatchUpdateRandomRender = (formData) =>
    http.postRequest('/ai/renderImage/updateRandomMatch', formData, {
        headers: {'Content-Type': 'multipart/form-data'}
    });

/**
 * Fetch history submissions with pagination.
 * @param {number} pageNumber - Current page number.
 * @param {number} pageSize - Number of records per page.
 * @returns {Promise} - Promise resolving to the paginated submissions.
 */
export const fetchHistorySubmissions = (pageNumber, pageSize) =>
    http.getRequest('/ai/history', {
        pageNumber: pageNumber, pageSize: pageSize
    });

/**
 * Get the original input image by prompt ID.
 * @param {string} promptId - The ID of the prompt.
 * @returns {Promise} - Promise resolving to the original image blob.
 */
export const getOriginalImage = (promptId) =>
    http.getRequest(`/ai/render/input/${promptId}`, {}, {responseType: 'blob'});

/**
 * Get the result image by prompt ID.
 * @param {string} promptId - The ID of the prompt.
 * @returns {Promise} - Promise resolving to the result image blob.
 */
export const getResultImage = (promptId) =>
    http.getRequest(`/ai/render/result/${promptId}`, {}, {responseType: 'blob'});

/**
 * Get the mask image by prompt ID.
 * @param {string} promptId - The ID of the prompt.
 * @returns {Promise} - Promise resolving to the mask image blob.
 */
export const getMaskImage = (promptId) =>
    http.getRequest(`/ai/render/mask/${promptId}`, {}, {responseType: 'blob'});

/**
 * Get the rendered image by prompt ID.
 * @param {string} promptId - The ID of the prompt.
 * @returns {Promise} - Promise resolving to the rendered image blob.
 */
export const getRenderImage = (promptId) =>
    http.getRequest(`/ai/render/input/${promptId}`, {}, {responseType: 'blob'});

/**
 * Re-render an image by prompt ID.
 * @param {string} promptId - The ID of the prompt.
 * @returns {Promise} - Promise resolving when the re-render is complete.
 */
export const reRenderImage = (promptId) =>
    http.getRequest(`/ai/re-render-image/${promptId}`);

/**
 * Fetch the list of car wraps.
 * @returns {Promise} - Promise resolving to the list of car wraps.
 */
export const getWrapList = () => http.getRequest('/ai/car-wrap');

export const getAllWrapList = (brand) => http.getRequest('/ai/car-wrap/all', {}, {
    headers: {
        'x-app-brand': brand
    }
});

export const getAllBasicWrapList = (brand) => http.getRequest('/ai/car-wrap/basic', {}, {
    headers: {
        'x-app-brand': brand
    }
});


export const updateColor = (formData) =>
    http.patchRequest('/ai/car-wrap', formData);

// ---- Swatch-book colour adjustment (feature 'colors') ----

/** Original swatch as a blob. raw=true → full-res original for the editor base
 *  (avoids editing a compressed thumbnail); raw=false → small display thumbnail. */
export const getSwatchImageBlob = (colorId, raw = true) =>
    http.getRequest(`/ai/car-wrap/swatch/${colorId}`, { raw }, { responseType: 'blob' });

/** Edited (reference) swatch as a blob; 404/500 when the colour has none. */
export const getRefSwatchImageBlob = (colorId, raw = true) =>
    http.getRequest(`/ai/car-wrap/ref-swatch/${colorId}`, { raw }, { responseType: 'blob' });

/** Upload the adjusted swatch (PNG blob from the editor, or a raw file).
 *  The OSS key extension is taken from the filename, so keep it matched to the
 *  actual bytes (a .jpg name over PNG bytes causes a format/extension mismatch). */
export const uploadReferenceSwatch = (colorId, imageBlobOrFile) => {
    const formData = new FormData();
    formData.append('colorId', colorId);
    const type = imageBlobOrFile.type || 'image/png';
    const ext = type.includes('png') ? 'png' : type.includes('webp') ? 'webp' : 'jpg';
    const filename = imageBlobOrFile.name || `${colorId}-ref.${ext}`;
    formData.append('image', imageBlobOrFile, filename);
    return http.postRequest('/ai/car-wrap/reference-swatch', formData, { timeout: 60000 });
};

/** Remove the edited swatch — renders fall back to the original swatch. */
export const deleteReferenceSwatch = (colorId) =>
    http.deleteRequest(`/ai/car-wrap/reference-swatch/${colorId}`);

/**
 * Save the composite image to the backend.
 * @param {string} promptId - The ID of the prompt associated with the image.
 * @param {string} imageData - The image data in Base64 format.
 * @returns {Promise} - Promise resolving when the image is successfully saved.
 */
export const saveCompositeImage = (promptId, imageData) =>
    http.postRequest(`/ai/render/save/${promptId}`, {image: imageData});


export const submitDataset = (formData) =>
    http.postRequest('/ai/training/image/batch', formData, {
        headers: {'Content-Type': 'multipart/form-data'}
    });

export const getDatasetList = () => http.getRequest('/ai/training/dataset');


export const triggerTrainingRender = (formData) =>
    http.postRequest('/ai/training/render/trigger', formData);


export const getDataImage = (id) =>
    http.getRequest(`/ai/training/result/${id}`, {}, {responseType: 'blob'});

export const getInputImage = (id) =>
    http.getRequest(`/ai/training/input/${id}`, {}, {responseType: 'blob'});

export const deleteDataset = (id) => {
    http.deleteRequest(`/ai/training/image/${id}`); // Delete a color wrap
};

export const submitSwatchBookImage = (formData) =>
    http.postRequest('/ai/training/swatch-book', formData, {
        headers: {'Content-Type': 'multipart/form-data'}
    });

export const batchUploadSwatchBookImages = (formData) =>
    http.postRequest('/ai/training/swatch-book/batch', formData, {
        headers: {'Content-Type': 'multipart/form-data'}
    });

export const getSwatchBookImage = (id) =>
    http.getRequest(`/ai/training/swatch-book/${id}`, {}, {responseType: 'blob'});

export const deleteSwatchBookImage = (id) => {
    http.deleteRequest(`/ai/training/swatch-book/${id}`); // Delete a color wrap
};

export const triggerSwatchBookRender = (formData) =>
    http.postRequest('/ai/training/swatch-book/trigger', formData);


export const getStitchImageFile = (id) =>
    http.getRequest(`/ai/training/stitch/${id}`, {}, {responseType: 'blob'});


